import imgGlobalTeam from '@assets/img/bg-about-global-team.webp'
import TeamHeroImg from '@assets/img/team-hero.webp'
import { dehydrate, QueryClient } from 'react-query'
import { getLegalDocs, getTeamPage } from 'src/@shared/service/cms-api'
import AboutImage from '@assets/img/about-casper.webp'
import { Col, Container, Row } from 'react-bootstrap'
import AboutFounders from '@modules/common/components/Founders/AboutFounders'

import CustomHead from '@atk/components/head/customHead/customHead'
import Newsletter2 from '@atk/components/newsletter/Newsletter2/Newsletter2'
import Hero from '@atk/components/hero/Hero/Hero'
import BadgeList from '@atk/components/ui/BadgeList/BadgeList'

import FramerMotion from '@atk/hooks/framerMotion'

import TeamList from '../modules/teams/components/TeamList/TeamList'
import useTeamList from '../modules/teams/hooks/useTeamList'

const initialArgs = { sort: 'position', limit: 0, page: 1 }

export async function getServerSideProps() {
    const queryClient = new QueryClient()
    // prefetch data on the server
    await Promise.all([
        queryClient.fetchQuery(['legaldocs'], () => getLegalDocs(initialArgs)),
        queryClient.fetchQuery(['teams'], () => getTeamPage()),
    ])
    return {
        props: {
            // dehydrate query cache
            dehydratedState: dehydrate(queryClient),
        },
    }
}

export default function Team() {
    const { founders, executives, members, catList, category, handleBadgeListClick } = useTeamList()
    return (
        <>
            <CustomHead
                title="Meet the Team Driving Enterprise Blockchain | Casper Labs"
                description="Meet the dynamic Casper Labs team driving blockchain technology forward for enterprises. Discover the experts shaping the future of decentralized solutions."
            >
                <link rel="preload" href={TeamHeroImg.src} as="image" />
            </CustomHead>
            <FramerMotion animation="fadeIn">
                <div className="teamsPage">
                    <main className="overflow-hidden">
                        <Hero
                            customClass="pt-0 pb-0 header heroTeam"
                            isHeader
                            bgImage={TeamHeroImg.src}
                            col1={6}
                            col2={6}
                            halfTitle="OUR TEAM"
                            title='Our Team of <span class="text-green">Experts</span>'
                            desc="A truly global team, Casper Labs is dedicated to delivering impactful, innovative solutions that solve some of today's most pressing problems. We pride ourselves on our diverse backgrounds and experiences — and the unique blend of skills that they enable."
                        ></Hero>
                        {founders && (
                            <div className="position-relative">
                                <FramerMotion>
                                    <AboutFounders founders={founders.members} />
                                </FramerMotion>
                            </div>
                        )}
                        <Container>
                            <section className="pt-3 pt-xl-6 pb-0">
                                <div className="our-leadership position-relative">
                                    <FramerMotion>
                                        <h2 className="h1 mb-5 mb-lg-6">Our Leadership</h2>
                                    </FramerMotion>
                                    {executives && (
                                        <TeamList list={executives.members} categoryClass="bgTeam"></TeamList>
                                    )}
                                </div>
                            </section>

                            <section className="our-team pt-6 pt-lg-7">
                                <FramerMotion y={400}>
                                    <p className="h5">Our Team</p>
                                    <hr />
                                    <Row className="my-5 align-items-center">
                                        <Col md={12} className="mb-4 mb-lg-0">
                                            {catList && catList.length > 0 && (
                                                <BadgeList
                                                    list={catList}
                                                    activeItem={category}
                                                    onClick={(item: string) => handleBadgeListClick(item)}
                                                />
                                            )}
                                        </Col>
                                    </Row>
                                </FramerMotion>
                                {members &&
                                    members.map((member, index) => {
                                        const show: boolean = category === member.category.meta?.slug ?? ''
                                        if (show)
                                            return (
                                                <TeamList
                                                    key={index}
                                                    list={member.members}
                                                    categoryClass="bgTeam"
                                                ></TeamList>
                                            )
                                    })}
                            </section>
                        </Container>
                        <FramerMotion>
                            <Hero
                                customClass="mt-5 pt-3 pb-0 globalTeam"
                                colLg1={6}
                                colLg2={6}
                                title="We are one global team"
                                desc="Our growing team of nearly 100 employees spans three continents and countless time zones. We’re committed to building the most reliable and sustainable blockchain for businesses by meeting organizations where they are, not vice versa."
                                linkButton={{
                                    label: 'Join our team',
                                    link: 'careers?section=jobs',
                                    variant: 'primary',
                                }}
                                bgImage={imgGlobalTeam.src}
                            ></Hero>
                        </FramerMotion>
                        <FramerMotion>
                            <Hero
                                customClass="mt-0 pt-0 pb-0 aboutCasperLabs"
                                colLg1={6}
                                colLg2={6}
                                title="About <span class='text-red'>Casper Labs</span>"
                                desc="A growing number of organizations, from fast-growth companies to the public sector and Fortune 500 are choosing to build on Casper, thanks to its completely secure and future-proof architecture."
                                linkButton={{ label: 'About Us', link: '/company', variant: 'secondary' }}
                                bgImage={AboutImage.src}
                            ></Hero>
                        </FramerMotion>

                        <Newsletter2 col1={5} col2={7} title="Sign up for our newsletter!" />
                    </main>
                </div>
            </FramerMotion>
        </>
    )
}
